import { Disclosure } from '@headlessui/react'
import classNames from 'classnames'
import { compact, startCase } from 'lodash'
import { ReactElement } from 'react'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import { PersonName } from 'app/components/activities/PersonName'
import { PersonOutcomes } from 'app/components/activities/PersonOutcomes'
import { ChevronToggle } from 'app/components/ChevronToggle'
import TrackableIcon from 'app/components/Icon/TrackableIcon'
import { formatDate } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { ActivityJson } from 'app/types/entities'
import type { CallJson, PersonSummary } from 'app/types/entities'
import { useUser, useWorkspace } from 'app/utils'

type Props = {
  ai: CallJson[`ai`]
  activity: ActivityJson
}

/**
 * Renders AI summary content for call cards.
 * Shows summary, action items, extraction date (super users only), and people identified.
 */
export function AiSummary({ ai, activity }: Props): ReactElement | null {
  const me = useUser()
  const workspace = useWorkspace()

  if (!me) return null
  if (!ai) return null

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  const LinkedContact = ({
    contacts,
    person,
  }: {
    contacts: ActivityJson[`contacts`]
    person?: PersonSummary
  }): ReactElement | null => {
    if (!person && contacts?.length) {
      return (
        <span className="mt-0.5 w-fit break-all rounded border bg-surface-subtle px-1.5 text-ui-xs capitalize leading-4 text-strong">
          {contacts[0].name?.toLocaleLowerCase()}
        </span>
      )
    }
    const contact = contacts?.find((contact) => contact.name === person?.name)
    if (!contact) return null
    return (
      <a
        className="mt-0.5 w-fit break-all rounded border bg-surface-subtle px-1.5 text-ui-xs capitalize leading-4 text-strong"
        href={`/contacts?drawer=${contact.id}&wId=${workspace?.id || null}`}
        target="_blank"
        rel="noreferrer"
      >
        {contact.name}
      </a>
    )
  }

  return (
    <div className="flex flex-col gap-y-3 px-4">
      <div className="mt-3 flex w-full flex-col gap-y-1.5">
        <span className="text-body-xs text-subtle">Summary</span>
        <span className="text-body-xs text-strong">{ai.summary || `-`}</span>
      </div>
      <div className="mt-3 flex w-full flex-col gap-y-1.5">
        <span className="text-body-xs text-subtle">Action Items</span>
        <span className="text-body-xs text-strong">{ai.actionItems || `-`}</span>
      </div>
      {me.type === `super` && (
        <div className="mt-3 flex w-full flex-col gap-y-1.5">
          <span className="text-body-xs text-subtle">Extraction Date</span>
          <span className="text-body-xs text-strong">
            {ai.extractionDate
              ? formatDate({
                  value: ai.extractionDate,
                  useLocalUtc: true,
                })
              : `-`}
          </span>
        </div>
      )}
      {ai.people && ai.people.length > 0 && (
        <div className="flex w-full flex-col gap-y-3">
          <div className="flex items-center gap-x-2">
            <span className="text-body-xs text-subtle">People identified by</span>
            <TrackableIcon className="h-5 w-5 rounded-md bg-inverse bg-gradient-to-br p-1 text-inverse" />
            <span className="text-body-xs text-subtle">A.I.</span>
          </div>
          <div className="flex flex-col gap-y-1">
            {ai.people
              .map((p, i) => ({ ...p, originalIndex: i }))
              .sort((person) => (person.isCaller ? -1 : 1))
              .map((person) => (
                <Disclosure
                  as="div"
                  key={`${person.name}-${person.originalIndex}`}
                  className="rounded-lg border border-default bg-surface-subtle shadow-elevation-1"
                >
                  {({ open }) => (
                    <>
                      <Disclosure.Button
                        className={classNames(
                          `flex w-full flex-col gap-y-2 px-4 py-2`,
                          open && `border-b border-default`,
                        )}
                      >
                        <div className="flex w-full items-center gap-x-1">
                          <PersonName person={person} isCaller={!!(person.isCaller && ai.people!.length > 1)} />
                          <div className="ml-auto flex shrink-0 items-center gap-x-2">
                            <ChevronToggle open={open} />
                          </div>
                        </div>
                        <PersonOutcomes
                          activity={activity}
                          person={person}
                          personIndex={person.originalIndex}
                          workspaceId={activity.workspaceId}
                        />
                      </Disclosure.Button>
                      <Disclosure.Panel className="overflow-hidden rounded-b-lg bg-surface px-4 py-3">
                        <KeyValueDrawer
                          map={{
                            Name: startCase(person.name || `Unknown`),
                            Phone: formatPhoneNumber(person.phone),
                            Relationship: person.relationship,
                            'Birth Date': formatDate({ value: person.birthDate, dateOnly: true }),
                            'Patient Status': person.patientType,
                            Treatment: person.treatment || person.serviceRequested,
                            'Apt. Schedule': person.appointmentScheduled ? `Yes` : `No`,
                            'Apt. Date': formatDate({ value: person.appointmentDate, dateOnly: true }),
                            Time: person.appointmentTime,
                            'Insurance Provider': person.insuranceProvider,
                            'Linked Contact':
                              compact(ai.people).length > 0
                                ? LinkedContact({ contacts: activity.contacts!, person })
                                : undefined,
                          }}
                          className={{}}
                        />
                      </Disclosure.Panel>
                    </>
                  )}
                </Disclosure>
              ))}
          </div>
        </div>
      )}
      {(!ai.people || ai.people.length === 0) && (
        <div className="flex w-full flex-col gap-y-3">
          <div className="flex items-center gap-x-2">
            <span className="text-body-xs text-subtle">Analyzed by</span>
            <TrackableIcon className="h-5 w-5 rounded-md bg-inverse bg-gradient-to-br p-1 text-inverse" />
            <span className="text-body-xs text-subtle">A.I.</span>
          </div>
        </div>
      )}
    </div>
  )
}
